import React from 'react';
import { cn } from '../../../lib/utils';

type Variant = 'default' | 'secondary' | 'outline' | 'ghost' | 'link' | 'destructive';
type Size = 'sm' | 'md' | 'lg' | 'icon';

export interface TaskyButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  isLoading?: boolean;
}

const variantClass: Record<Variant, string> = {
  default:
    'bg-primary text-white hover:bg-primary-hover rounded-tasky-sm shadow-sm',
  secondary:
    'bg-secondary text-dark hover:bg-gray-200 rounded-tasky-sm',
  outline:
    'bg-gray-200 text-dark hover:bg-gray-300 font-medium rounded-tasky-sm',
  ghost:
    'text-gray-600 hover:text-dark hover:bg-warm-hover rounded-tasky-sm',
  link:
    'text-primary underline underline-offset-4 hover:no-underline px-0 h-auto',
  destructive:
    'bg-destructive text-white hover:bg-red-700 rounded-tasky-sm shadow-sm',
};

const sizeClass: Record<Size, string> = {
  sm: 'h-9 px-3 text-sm',
  md: 'h-10 px-4 text-base',
  lg: 'h-11 px-8 text-lg',
  icon: 'h-10 w-10 flex items-center justify-center p-0',
};

export const TaskyButton = React.forwardRef<HTMLButtonElement, TaskyButtonProps>(
  (
    { variant = 'default', size = 'md', className, disabled, isLoading, children, ...rest },
    ref,
  ) => (
    <button
      ref={ref}
      {...rest}
      disabled={disabled || isLoading}
      className={cn(
        'inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
        '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
        variantClass[variant],
        sizeClass[size],
        (disabled || isLoading) &&
          'bg-gray-300 text-gray-600 cursor-not-allowed hover:bg-gray-300 shadow-none',
        className,
      )}
    >
      {isLoading && (
        <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
      )}
      {children}
    </button>
  ),
);
TaskyButton.displayName = 'TaskyButton';
